import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import apiService from '../../services/api';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { RequiredLabel } from '../common/FormTextField';

interface SessionEditDialogProps {
  open: boolean;
  calender: any | null;
  onClose: () => void;
  onSave: () => void;
}

export interface EscCalenderForm {
  title: string;
  description?: string;

  startDate?: string;
  endDate?: string;
  venue?: string;
  scheme?: string;
  applyLink?: string;

  startTime: Dayjs | null;
  endTime: Dayjs | null;
}

const FloorEditDialog: React.FC<SessionEditDialogProps> = ({
  open,
  calender,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    getValues,
  } = useForm<EscCalenderForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      venue: '',
      scheme: '',
      applyLink: '',
      startTime: null,
      endTime: null,
    },
  });

  useEffect(() => {
    if (calender && open) {
      reset({
        title: calender.title,
        description: calender.description || '',

        startDate: calender.startDate || '',
        endDate: calender.endDate || '',
        venue: calender.venue || '',
        scheme: calender.scheme || '',
        applyLink: calender.applyLink || '',

        startTime: calender.startTime
          ? dayjs(calender.startTime, 'HH:mm')
          : null,
        endTime: calender.endTime ? dayjs(calender.endTime, 'HH:mm') : null,
      });
    } else if (!calender && open) {
      reset({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        venue: '',
        scheme: '',
        applyLink: '',
        startTime: '',
        endTime: '',
      });
    }
  }, [calender, open, reset]);

  const onSubmit = async (data: EscCalenderForm) => {
    setLoading(true);
    setError('');

    try {
      const formattedData = {
        title: data.title.trim(),
        description: data.description?.trim(),

        startDate: data.startDate,
        endDate: data.endDate,
        venue: data.venue?.trim(),
        scheme: data.scheme?.trim(),
        applyLink: data.applyLink?.trim(),

        startTime: data.startTime
          ? dayjs(data.startTime).format('HH:mm:ss')
          : null,
        endTime: data.endTime ? dayjs(data.endTime).format('HH:mm:ss') : null,
      };

      const isEditing = !!calender;

      await (isEditing
        ? apiService.updateEscCalender(calender.id, formattedData)
        : apiService.createEscCalender(formattedData));

      toast.success(
        `ESC Calendar ${isEditing ? 'updated' : 'created'} successfully`
      );

      onSave();
      onClose();
    } catch (err: any) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      setError(apiError);
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {calender ? 'Edit ESC Calendar' : 'Create ESC Calendar'}
        <IconButton onClick={onClose} sx={{ float: 'right' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {error && <Alert severity="error">{error}</Alert>}

          <Grid container spacing={3}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                {...register('title', {
                  required: 'ESC Calendar title is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                })}
                label={<RequiredLabel label="ESC Calendar Title" />}
                fullWidth
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                {...register('description', {
                  maxLength: {
                    value: 250,
                    message: 'Maximum 250 characters allowed',
                  },
                })}
                label="ESC Calendar Description"
                fullWidth
                multiline
                rows={4}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </Grid>

            {/* start date */}
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('startDate', { required: 'Start date required' })}
                label={<RequiredLabel label="Start Date" />}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
                inputProps={{
                  min: dayjs().format('YYYY-MM-DD'),
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('endDate', {
                  required: 'End date required',
                  validate: value => {
                    const startDate = getValues('startDate');

                    if (!value) return 'End date required';
                    if (!startDate) return true;

                    return (
                      value >= startDate || 'End date must be after start date'
                    );
                  },
                })}
                label={<RequiredLabel label="End Date" />}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.endDate}
                helperText={errors.endDate?.message}
                inputProps={{
                  min: getValues('startDate') || dayjs().format('YYYY-MM-DD'),
                }}
              />
            </Grid>
            {/*Start Time */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="startTime"
                  control={control}
                  rules={{ required: 'Start time required' }}
                  render={({ field }) => (
                    <TimePicker
                      label={<RequiredLabel label="Start Time" />}
                      {...field}
                      ampm={true}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Enter start time"
                          error={!!errors.startTime}
                          helperText={errors.startTime?.message}
                        />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* End time */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="endTime"
                  control={control}
                  rules={{
                    required: 'End time required',
                    validate: value => {
                      if (!value) return 'End time required';
                      const st = dayjs(getValues('startTime'));
                      const et = dayjs(value);
                      return (
                        et.isAfter(st) || 'End time must be after start time'
                      );
                    },
                  }}
                  render={({ field }) => (
                    <TimePicker
                      label={<RequiredLabel label="End Time" />}
                      ampm={true}
                      {...field}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Enter end time"
                          error={!!errors.endTime}
                          helperText={errors.endTime?.message}
                        />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('venue')}
                label="Venue"
                fullWidth
                error={!!errors.venue}
                helperText={errors.venue?.message}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField {...register('scheme')} label="Scheme" fullWidth />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...register('applyLink', {
                  validate: value => {
                    if (!value) return true;

                    try {
                      new URL(value);
                      return true;
                    } catch {
                      return 'Enter valid URL (include http/https)';
                    }
                  },
                })}
                label="Apply Link"
                fullWidth
                error={!!errors.applyLink}
                helperText={errors.applyLink?.message}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>

          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <CircularProgress size={16} />
            ) : calender ? (
              'Update ESC Calendar'
            ) : (
              'Create ESC Calendar'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FloorEditDialog;
